import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Autocomplete,
  TextField,
  Box,
  CircularProgress,
  Typography,
} from '@mui/material';
import api from '../../services/api';

/**
 * LocationSelector Component
 * 
 * İl-İlçe-Mahalle cascade seçimi için Material-UI Autocomplete kullanır
 * 
 * Props:
 * - question: Question object with dependsOn, source, etc.
 * - value: Current selected value (JSON string or object)
 * - onChange: Callback function (key, value)
 * - answers: All answers object (for dependsOn lookup)
 * - allQuestions: All questions array (for finding parent by dependsOn ID)
 * - required: Boolean
 * - disabled: Boolean
 */
const LocationSelector = ({
  question,
  value,
  onChange,
  answers = {},
  allQuestions = [],
  required = false,
  disabled = false,
}) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedValue, setSelectedValue] = useState(null);
  const [error, setError] = useState(null);

  // Parse value from JSON string or object
  const parseValue = useCallback((val) => {
    if (!val) return null;
    if (typeof val === 'string') {
      try {
        return JSON.parse(val);
      } catch {
        return null;
      }
    }
    return val;
  }, []);

  // Get current location value based on source
  const getCurrentLocationValue = useCallback(() => {
    const parsedValue = parseValue(value);
    if (!parsedValue) return null;

    switch (question.source) {
      case 'il':
        return parsedValue.il || null;
      case 'ilce':
        return parsedValue.ilce || null;
      case 'mahalle':
        return parsedValue.mahalle || null;
      default:
        return null;
    }
  }, [value, question.source, parseValue]);

  // Get parent location code for cascade
  const getParentCode = useCallback(() => {
    if (!question.dependsOn) return null;

    // Find parent question: first try _parentQuestion, then search by dependsOn ID
    let parentQuestion = question._parentQuestion || null;
    
    if (!parentQuestion && allQuestions.length > 0) {
      // Find parent question by dependsOn ID
      parentQuestion = allQuestions.find(
        (q) =>
          (q._id && q._id.toString() === question.dependsOn.toString()) ||
          (q._tempId && q._tempId === question.dependsOn) ||
          (q.key === question.dependsOn) // Fallback
      ) || null;
    }
    
    if (!parentQuestion) return null;

    const parentAnswer = answers[parentQuestion.key];
    if (!parentAnswer) return null;

    // Parse parent answer
    const parentParsed = parseValue(parentAnswer);
    if (!parentParsed) return null;

    // Get parent's location code based on parent's source
    if (question.source === 'ilce') {
      // Parent is 'il'
      return parentParsed.il?.code || null;
    } else if (question.source === 'mahalle') {
      // Parent is 'ilce'
      return parentParsed.ilce?.code || null;
    }

    return null;
  }, [question.dependsOn, question.source, answers, parseValue, allQuestions, question._parentQuestion]);

  const parentLocationCode = useMemo(() => {
    return getParentCode();
  }, [getParentCode]);

  // Load options from API
  const loadOptions = useCallback(async () => {
    if (!question.source) {
      setOptions([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let response;
      const parentCode = parentLocationCode;

      if (question.source === 'il') {
        // Load all İl
        response = await api.get('/locations/iller');
      } else if (question.source === 'ilce') {
        // Load İlçeler by İl code
        if (!parentCode) {
          setOptions([]);
          setLoading(false);
          return;
        }
        response = await api.get('/locations/ilceler', {
          params: { ilCode: parentCode },
        });
      } else if (question.source === 'mahalle') {
        // Load Mahalleler by İlçe code
        if (!parentCode) {
          setOptions([]);
          setLoading(false);
          return;
        }
        response = await api.get('/locations/mahalleler', {
          params: { ilceCode: parentCode },
        });
      } else {
        setOptions([]);
        setLoading(false);
        return;
      }

      if (response.data.success) {
        setOptions(response.data.data || []);
      } else {
        setError('Veri yüklenemedi');
      }
    } catch (err) {
      console.error('LocationSelector loadOptions error:', err);
      setError(err.response?.data?.message || 'Veri yüklenirken hata oluştu');
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, [question.source, parentLocationCode]);

  // Load options when source or parent changes
  useEffect(() => {
    loadOptions();
  }, [loadOptions]);

  // Update selected value when value prop changes
  useEffect(() => {
    const currentLocation = getCurrentLocationValue();
    if (currentLocation) {
      // Find matching option
      const option = options.find(
        (opt) => opt.code === currentLocation.code
      );
      setSelectedValue(option || null);
    } else {
      setSelectedValue(null);
    }
  }, [value, options, getCurrentLocationValue]);

  // Handle selection change
  const handleChange = (event, newValue) => {
    setSelectedValue(newValue);

    // Build location object
    const parsedValue = parseValue(value) || {};
    const newLocationValue = { ...parsedValue };

    if (question.source === 'il') {
      newLocationValue.il = newValue
        ? { code: newValue.code, name: newValue.name }
        : null;
      // Clear dependent values
      newLocationValue.ilce = null;
      newLocationValue.mahalle = null;
    } else if (question.source === 'ilce') {
      newLocationValue.ilce = newValue
        ? { code: newValue.code, name: newValue.name }
        : null;
      // Clear dependent values
      newLocationValue.mahalle = null;
    } else if (question.source === 'mahalle') {
      newLocationValue.mahalle = newValue
        ? { code: newValue.code, name: newValue.name }
        : null;
    }

    // Convert to JSON string and call onChange
    const jsonValue = JSON.stringify(newLocationValue);
    if (onChange) {
      onChange(question.key, jsonValue);
    }
  };

  // Check if parent is selected (for cascade)
  const isParentSelected = () => {
    if (!question.dependsOn) return true;

    // Find parent question: first try _parentQuestion, then search by dependsOn ID
    let parentQuestion = question._parentQuestion || null;
    
    if (!parentQuestion && allQuestions.length > 0) {
      // Find parent question by dependsOn ID
      parentQuestion = allQuestions.find(
        (q) =>
          (q._id && q._id.toString() === question.dependsOn.toString()) ||
          (q._tempId && q._tempId === question.dependsOn) ||
          (q.key === question.dependsOn) // Fallback
      ) || null;
    }
    
    if (!parentQuestion) return false;

    const parentAnswer = answers[parentQuestion.key];
    if (!parentAnswer) return false;

    const parentParsed = parseValue(parentAnswer);
    if (!parentParsed) return false;

    if (question.source === 'ilce') {
      return !!parentParsed.il;
    } else if (question.source === 'mahalle') {
      return !!parentParsed.ilce;
    }

    return true;
  };

  const parentSelected = isParentSelected();
  const isDisabled = disabled || !parentSelected || loading;

  return (
    <Box>
      <Autocomplete
        options={options}
        getOptionLabel={(option) => option.name || ''}
        value={selectedValue}
        onChange={handleChange}
        loading={loading}
        disabled={isDisabled}
        isOptionEqualToValue={(option, value) => option.code === value.code}
        renderInput={(params) => (
          <TextField
            {...params}
            required={required}
            error={required && !selectedValue}
            helperText={
              error ||
              (!parentSelected
                ? 'Önce üst seviye konum seçilmelidir'
                : question.helperText || '')
            }
            placeholder={
              question.placeholder ||
              (question.source === 'il'
                ? 'İl seçiniz'
                : question.source === 'ilce'
                ? 'İlçe seçiniz'
                : 'Mahalle seçiniz')
            }
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        noOptionsText={
          loading
            ? 'Yükleniyor...'
            : !parentSelected
            ? 'Önce üst seviye konum seçiniz'
            : 'Seçenek bulunamadı'
        }
      />
      {!parentSelected && question.dependsOn && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
          Önce {question.source === 'ilce' ? 'İl' : 'İlçe'} seçilmelidir
        </Typography>
      )}
    </Box>
  );
};

LocationSelector.propTypes = {
  question: PropTypes.object.isRequired,
  value: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  answers: PropTypes.object,
  allQuestions: PropTypes.array,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
};

LocationSelector.defaultProps = {
  value: null,
  answers: {},
  allQuestions: [],
  required: false,
  disabled: false,
};

export default LocationSelector;

